import {
  Badge,
  Card,
  ErrorState,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import { ApiException } from '@/lib/api/types';
import { formatDateTime } from '@/lib/format';
import { useHealth } from './useHealth';
import type { HealthStatus, Saturation } from './types';

/**
 * 전 구간 연결 상태 화면.
 *
 * Phase 0의 검증 대상을 그대로 보여준다.
 *   브라우저 → nginx → React → /api/health → Spring Boot → MySQL/Redis
 *
 * 모니터링 페이지 1단계이기도 하다. 지금은 Saturation만 표시하고,
 * Errors(배치 실패·예외 이력)는 배치가 생기는 Phase 1에서 추가한다.
 * (00-architecture.md 8.3절)
 */
export function HealthPanel() {
  const { data, isLoading, error, refetch, dataUpdatedAt } = useHealth();

  if (isLoading) {
    return (
      <Card title="연결 상태">
        <Spinner label="상태 확인 중" />
      </Card>
    );
  }

  if (error || !data) {
    // 여기 걸리는 것은 네트워크 단절이나 500이다.
    // 구성요소 장애(503)는 정상 응답으로 처리되어 아래 표에 DOWN으로 표시된다.
    const message =
      error instanceof ApiException
        ? error.message
        : '상태를 불러오지 못했습니다.';
    return (
      <Card title="연결 상태">
        <ErrorState message={message} onRetry={() => void refetch()} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card
        title="연결 상태"
        action={<StatusBadge status={data.status} />}
      >
        <Table>
          <thead>
            <tr>
              <Th>구성요소</Th>
              <Th>상태</Th>
              <Th align="right">응답</Th>
              <Th>상세</Th>
            </tr>
          </thead>
          <tbody>
            {data.components.map((component) => (
              <tr key={component.name}>
                <Td className="font-medium">{component.name}</Td>
                <Td>
                  <StatusBadge status={component.status} />
                </Td>
                <Td align="right" numeric>
                  {component.latencyMs}ms
                </Td>
                <Td className="text-xs text-fg-muted">{component.detail}</Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <dl className="mt-4 grid grid-cols-2 gap-2 text-xs text-fg-muted">
          <div>
            <dt className="inline">버전 </dt>
            <dd className="inline text-fg">{data.version}</dd>
          </div>
          <div>
            <dt className="inline">가동 </dt>
            <dd className="inline text-fg">{formatUptime(data.uptimeSeconds)}</dd>
          </div>
          <div>
            <dt className="inline">서버 시각 </dt>
            <dd className="inline text-fg">{formatDateTime(data.timestamp)}</dd>
          </div>
          <div>
            <dt className="inline">마지막 확인 </dt>
            <dd className="inline text-fg">
              {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('ko-KR') : '-'}
            </dd>
          </div>
        </dl>
      </Card>

      <SaturationCard saturation={data.saturation} />
    </div>
  );
}

function StatusBadge({ status }: { status: HealthStatus }) {
  return (
    <Badge tone={status === 'UP' ? 'success' : 'danger'}>
      {status === 'UP' ? '정상' : '장애'}
    </Badge>
  );
}

/**
 * 포화도 카드.
 *
 * 사용자 1명 시스템에서 실제 위험은 트래픽 급증이 아니라
 * 8GB RAM과 디스크가 말라가는 것이다. (00-architecture.md 8.1절)
 */
function SaturationCard({ saturation }: { saturation: Saturation }) {
  const diskUsedPercent =
    saturation.diskTotalGb === 0
      ? 0
      : ((saturation.diskTotalGb - saturation.diskFreeGb) / saturation.diskTotalGb) * 100;

  // 판단 기준은 실사용(GC 직후)이다. 할당량만 보면 "안 치웠을 뿐"인 상태를
  // 메모리 부족으로 잘못 읽는다. GC가 아직 안 돌았을 때만 할당량으로 대체한다.
  const measured = saturation.heapLiveMb != null && saturation.heapLivePercent != null;
  const heapMb = saturation.heapLiveMb ?? saturation.heapUsedMb;
  const heapPercent = saturation.heapLivePercent ?? saturation.heapUsedPercent;

  return (
    <Card title="포화도 (Saturation)">
      <div className="flex flex-col gap-4">
        <div>
          <Meter
            label={measured ? 'JVM 힙 · 실사용' : 'JVM 힙 · 할당 (GC 대기)'}
            percent={heapPercent}
            detail={`${heapMb} / ${saturation.heapMaxMb} MB`}
          />
          <p className="mt-1 text-xs text-fg-subtle">
            {measured ? (
              <>
                할당 <span className="tabular text-fg-muted">{saturation.heapUsedMb} MB</span>
                {' '}({saturation.heapUsedPercent.toFixed(1)}%) — 버려졌지만 아직 안 치운 객체를 포함한 값
                {' · '}
                GC <span className="tabular text-fg-muted">{saturation.gcCount}회</span>
                {' / '}
                <span className="tabular text-fg-muted">{formatGcTime(saturation.gcTimeMs)}</span>
              </>
            ) : (
              'GC가 아직 한 번도 돌지 않아 실사용량을 알 수 없습니다. 지금 값은 할당량이라 실제보다 높게 보입니다.'
            )}
          </p>
        </div>
        <Meter
          label="디스크"
          percent={diskUsedPercent}
          detail={`여유 ${saturation.diskFreeGb} / ${saturation.diskTotalGb} GB`}
        />
      </div>
      <div className="mt-3 space-y-1 text-xs text-fg-subtle">
        <p>
          <span className="font-medium">실사용</span>은 마지막 청소(GC) 직후 남아 있던 양이고,{' '}
          <span className="font-medium">할당</span>은 청소 전 기준이라 항상 더 큽니다.
          위험 여부는 실사용으로 판단합니다 — 60% 주황, 80% 빨강. 최대치는 -Xmx768m 설정값입니다.
        </p>
        <p>오류(5xx) 이력은 '오류' 탭에서 봅니다. Latency · Traffic 지표는 아직 없습니다.</p>
      </div>
    </Card>
  );
}

/** GC 누적 시간을 읽기 쉽게. 예: 1240 → "1.2초" */
function formatGcTime(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}초` : `${ms}ms`;
}

/**
 * 사용률 막대.
 *
 * 임계값 색상은 고정값이다.
 *   80% 이상 빨강 — 조치가 필요한 수준
 *   60% 이상 주황 — 지켜봐야 하는 수준
 * 8GB 미니PC에서 힙이 80%를 넘으면 GC가 잦아지며 응답이 눈에 띄게 느려진다.
 */
function Meter({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number;
  detail: string;
}) {
  const clamped = Math.min(100, Math.max(0, percent));
  const tone =
    clamped >= 80 ? 'bg-danger' : clamped >= 60 ? 'bg-warn' : 'bg-ok';

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs">
        <span className="font-medium text-fg">{label}</span>
        <span className="tabular text-fg-muted">
          {clamped.toFixed(1)}% · {detail}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-hover">
        <div
          className={cx('h-full rounded-full transition-all', tone)}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

/** 초를 사람이 읽는 형태로 바꾼다. 예: 93784 → "1일 2시간" */
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}일 ${hours}시간`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
}
